import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  ListIcon,
  Button,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckCircleIcon, AddIcon } from '@chakra-ui/icons';
import { Role } from '../../types';

interface RoleCardProps {
  role: Role;
  onClick?: () => void;
  onAddChild?: () => void;
  showAddChild?: boolean;
  isSuperUser?: boolean;
  isClickable?: boolean;
  themeColor?: string;
}

// Logic from Directive 4: Translate Tailwind to Chakra
const getLevelStyles = (level: number, isDark: boolean, themeColor?: string) => {
  if (level === 0) return { borderColor: 'blue.500', bg: isDark ? 'blue.900' : 'blue.50', footerBg: isDark ? 'blue.900' : 'blue.50' };
  if (level === 1) return { borderColor: 'green.500', bg: isDark ? 'green.900' : 'green.50', footerBg: isDark ? 'green.900' : 'green.50' };
  if (themeColor) {
    const baseColor = themeColor.split('.')[0] || 'gray';
    return { borderColor: themeColor, bg: isDark ? 'gray.700' : 'white', footerBg: isDark ? 'gray.700' : `${baseColor}.50` };
  }

  return { borderColor: 'gray.400', bg: isDark ? 'gray.700' : 'white', footerBg: isDark ? 'gray.700' : 'gray.50' };
};

export const RoleCard = ({
  role,
  onClick,
  onAddChild,
  showAddChild = false,
  isSuperUser,
  isClickable = false,
  themeColor,
}: RoleCardProps) => {
  const isDark = useColorModeValue(false, true);
  const styles = getLevelStyles(role.level, isDark, themeColor);
  const cardBg = useColorModeValue('white', 'gray.800');

  return (
    <Box
      borderWidth="2px"
      borderRadius="lg"
      shadow="sm"
      _hover={{ shadow: 'md' }}
      transition="shadow 0.2s"
      borderColor={styles.borderColor}
      bg={cardBg}
    >
      <VStack
        align="stretch"
        p={4}
        cursor={isClickable ? 'pointer' : 'default'}
        onClick={isClickable ? onClick : undefined}
      >
        <Box textAlign="center" mb={3}>
          <Heading size="sm">{role.title}</Heading>
          {role.subtitle && (
            <Text fontSize="xs" fontStyle="italic" color="gray.500" mt={1}>
              {role.subtitle}
            </Text>
          )}
          {role.ownerName && (
            <Text fontSize="md" color="red.600" fontWeight="bold" mt={1}>
              {role.ownerName}
            </Text>
          )}
        </Box>

        <Box pl={4} textAlign="left">
          <List spacing={1} fontSize="sm" styleType="disc">
            {role.responsibilities.map((resp, idx) => (
              <ListItem key={idx} display="list-item">
                {resp}
              </ListItem>
            ))}
          </List>
        </Box>
        {role.responsibilities.length === 0 && (
          <Text fontSize="sm" color="gray.400" fontStyle="italic" textAlign="center">
            No responsibilities defined
          </Text>
        )}
      </VStack>

      {isSuperUser && showAddChild && onAddChild && (
        <Box borderTopWidth="1px" p={2} bg={styles.footerBg}>
          <Button
            w="full"
            variant="ghost"
            size="sm"
            colorScheme="blue"
            leftIcon={<AddIcon />}
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click
              onAddChild();
            }}
          >
            Add Role Below
          </Button>
        </Box>
      )}
    </Box>
  );
};

